import { Skeleton } from '@ifixit/ui/components/skeleton';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Pagination from 'Chakra/Pagination';
import { _js } from '@ifixit/utils/localize';
import { SectionHeading } from './section_heading';
import groupBy from 'lodash/groupBy';

export function Activity({ userid, data }: { userid: number; data: any }) {
   const { filters, pageContext: initialPageContext, activityTimelineURI: timelineImg } = data;

   return (
      <div className="flex flex-col gap-6">
         <ActiveHours image={timelineImg} />
         <ActivityFilterView
            userid={userid}
            filters={filters}
            initialPageContext={initialPageContext}
         />
      </div>
   );
}

const ActiveHours = ({ image }: { image: string }) => (
   <>
      <SectionHeading>{_js('Activity')}</SectionHeading>
      <div>
         <h3 className="heading-24">{_js('Active hours')}</h3>
         <img className="min-h-[65px]" src={image} alt={_js('Activity graph')} />
         <p>
            {_js(
               'The time of day that you have been active over the last 90 days. Each line on this graph \
            is an action you performed on the site (we only count major actions, like voting and \
            editing posts). The numbers on the bottom of the graph are the hour of the day in '
            )}
            <a href="https://greenwichmeantime.com/time-zone/usa/arizona/">
               {_js('Mountain Standard Time')}
            </a>
            .
         </p>
      </div>
   </>
);

const ActivityFilterView = ({
   userid,
   filters,
   initialPageContext,
}: {
   userid: number;
   filters: any;
   initialPageContext: any;
}) => {
   const [activeFilter, setActiveFilter] = useState('nofilter');
   const [pageContext, setPageContext] = useState(initialPageContext);

   const currentFilter = activeFilter || 'nofilter';
   const { data, isLoading } = useQuery({
      queryKey: [`users/${userid}/${pageContext.curPage}/${currentFilter}`],
   });

   if (!isLoading && (data as any).pageContext != pageContext) {
      setPageContext((data as any).pageContext);
   }

   const activityList = isLoading ? [] : (data as any).activities;
   const showPagination = pageContext.totalPages > 1;

   function changeFilter(newFilterName: string) {
      setActiveFilter(newFilterName);
      setPageContext({
         ...pageContext,
         curPage: 1,
      });
   }

   return (
      <div className="flex flex-col gap-4">
         <h3 className="heading-24">{_js('Activity list')}</h3>
         <FilterOptions activeFilter={activeFilter} options={filters} changeFilter={changeFilter} />
         {isLoading ? (
            <LoadingList />
         ) : (
            <ActivityList showPagination={showPagination} activities={activityList} />
         )}
         {showPagination && (
            <Pagination
               pageContext={pageContext}
               setPageContext={setPageContext}
               className="rounded-xl mt-2"
            />
         )}
      </div>
   );
};

const LoadingList = () => {
   const LIST_SIZE = 12;
   return (
      <div className="flex flex-col gap-2 my-2">
         {Array.from(Array.from({ length: LIST_SIZE }), (e, i) => (
            <Skeleton key={i} className="h-[19px]" />
         ))}
      </div>
   );
};

const ActivityList = ({
   showPagination,
   activities,
}: {
   showPagination: boolean;
   activities: any[];
}) => {
   const hasActivity = activities.length > 0;

   if (!hasActivity) {
      return <p>{_js('No user activity')}</p>;
   }
   return <ActivityHistory showPagination={showPagination} activities={activities} />;
};

const FilterOptions = ({
   activeFilter,
   options,
   changeFilter,
}: {
   activeFilter: any;
   options: any;
   changeFilter: (_newFilterName: string) => void;
}) => {
   const mainFilters = options.mainFilters;
   const extraFilters = options.extraFilters;

   return (
      <div className="mb-8 gradient-overflow min-md:max-w-full">
         <div className="flex items-center gap-1 gradient-overflow-inner">
            <FiltersList
               filters={mainFilters}
               activeFilter={activeFilter}
               changeFilter={changeFilter}
            />
            <FiltersDropdown filters={extraFilters} changeFilter={changeFilter} />
         </div>
      </div>
   );
};

const FiltersList = ({
   filters,
   activeFilter,
   changeFilter,
}: {
   filters: any;
   activeFilter: any;
   changeFilter: (_newFilterName: string) => void;
}) =>
   filters.map((filter: any) => (
      <button
         type="button"
         className={cn(
            'inline-flex items-center h-6 px-2 rounded-full border text-[12px] font-semibold whitespace-nowrap transition-colors cursor-pointer hover:bg-gray-100',
            filter.name === activeFilter
               ? 'bg-gray-100 border-gray-100'
               : 'bg-white border-gray-300'
         )}
         key={filter.name}
         onClick={() => changeFilter(filter.name)}
      >
         {filter.label}
      </button>
   ));

const FiltersDropdown = ({
   filters,
   changeFilter,
}: {
   filters: any;
   changeFilter: (_newFilterName: string) => void;
}) => (
   <select
      id="filtersDropdown"
      className="h-6 w-fit max-w-[170px] rounded-full border border-gray-300 bg-white px-2 py-0 text-[12px] font-semibold cursor-pointer"
      defaultValue=""
      onChange={e => changeFilter(e.target.value)}
   >
      <option value="" disabled>
         {_js('More Filter Options')}
      </option>
      {filters.map((filter: any) => (
         <option key={filter.name} value={filter.name}>
            {filter.label}
         </option>
      ))}
   </select>
);

function groupActivitiesByDay(activities: any[]) {
   return groupBy(activities, (curActivity: any) => {
      const date = new Date(curActivity.date * 1000);
      return date.toLocaleDateString();
   });
}

const ActivityHistory = ({
   showPagination,
   activities,
}: {
   showPagination: boolean;
   activities: any[];
}) => {
   const history = groupActivitiesByDay(activities);
   return (
      <div className={`flex flex-col gap-4 ${showPagination && 'mb-2'}`}>
         {Object.entries(history).map(([day, activities]) => (
            <ActivityHistoryGroup key={day} day={day} activities={activities} />
         ))}
      </div>
   );
};

const ActivityHistoryGroup = ({ day, activities }: { day: any; activities: any[] }) => (
   <>
      {activities.map((activity: any) => (
         <ActivityHistoryItem key={activity.id} activity={activity} day={day} />
      ))}
   </>
);

const ActivityHistoryItem = ({ activity, day }: { activity: any; day: any }) => {
   // Timestamps from the backend are off by a factor of 1000.
   const date = new Date(activity.date * 1000);
   const link = activity.link || activity.url;
   const delta = activity.delta;
   const showDelta = delta && delta != 0;
   const id = activity.docid && activity.event ? `${activity.event}${activity.docid}` : undefined;

   return (
      <div className="flex items-center gap-4" id={id}>
         {/* iconSvg is server-generated by Badge::getBadgeIconSvg() from hardcoded values only */}
         {activity.iconSvg ? (
            <div
               className="size-[48px] flex justify-center items-center cursor-pointer"
               onClick={() => (window.location.href = link)}
               dangerouslySetInnerHTML={{ __html: activity.iconSvg }}
            />
         ) : (
            <div className="size-[48px] gap-3 flex rounded-lg bg-gray-100 justify-center items-center">
               {showDelta && (
                  <div className={`font-semibold ${delta > 0 ? 'text-green-500' : 'text-red-500'}`}>
                     {delta > 0 ? `+${delta}` : delta}
                  </div>
               )}
            </div>
         )}
         <div className="flex flex-col text-14px text-black ">
            {link ? (
               <a className="font-semibold" href={link} rel="nofollow">
                  {activity.text}
               </a>
            ) : (
               <p>{activity.text}</p>
            )}
            <p className="m-0 text-12px text-gray-500 font-regular">
               {/* Translators: %1 -> date string (i.e. 10/28/2025) %2 -> time of day (i.e. 2:46) */}
               {_js('%1 at %2', day, date.toLocaleTimeString())}
            </p>
         </div>
      </div>
   );
};
